// 🎯 Presets de campagne pour les cartes du Radar d'opportunités.
// SOURCE UNIQUE : Radar (Dashboard + onboarding), Génie (Accueil), étape 4 onboarding.
// Doctrine : chaque clic atterrit sur une campagne DÉJÀ construite (nom, objectif,
// objet, corps, cible) — l'utilisateur relit et valide, il ne construit rien.
import { marronnierPresetBySlug } from './marronniers';

const sign = (company, owner) => owner || `L'équipe ${company}`;

/**
 * Construit `{ startStep, preset }` pour une carte du radar, ou null si la carte
 * n'est pas une action de campagne (ex. vœux automatiques → simple navigation).
 * @param {object} o carte d'opportunité du backend (key + méta event_key/gender/city…)
 * @param {object} ctx { company, owner, tone } — signature humaine + ton
 */
export function presetForOpportunity(o, ctx = {}) {
  const company = ctx.company || 'notre équipe';
  const owner = ctx.owner || null;
  const tone = ctx.tone || 'vouvoiement';
  const base = { channel: 'email', tone };

  switch (o?.key) {
    case 'marronnier': {
      const m = marronnierPresetBySlug(o.event_key);
      if (m) return { startStep: 1, preset: { ...base, ...m, tone: m.tone || tone } };
      // printemps / été : pas de gabarit dédié → offre de saison préconstruite
      const isEte = o.event_key === 'ete';
      return {
        startStep: 1,
        preset: {
          ...base,
          name: isEte ? 'Offre d\'été ☀️' : 'Offre de printemps 🌸',
          objective: 'Lancer une offre ou une promotion',
          subject: isEte ? '☀️ L\'été commence ici, {{prénom}}' : '🌸 Le printemps vous va bien, {{prénom}}',
          body: isEte
            ? `{{prénom}}, l'été est officiellement là ☀️\n\nEt chez nous, il commence par une attention de saison : cette semaine, une offre estivale vous attend.\n\nPassez nous voir avant qu'elle ne fonde au soleil.\n\nBel été !\n${sign(company, owner)}\n\nP.S. Les meilleures choses de l'été partent vite — celle-ci aussi.`
            : `{{prénom}}, ça sent le renouveau 🌸\n\nLe printemps est là — la saison parfaite pour se faire du bien et repartir du bon pied.\n\nPour fêter ça, une offre de saison vous attend cette semaine.\n\nPassez nous voir — le printemps ne dure jamais assez longtemps.\n\n${sign(company, owner)}`,
          filters: { exclude_recent: true },
        },
      };
    }
    case 'dormants':
      return {
        startStep: 1,
        preset: {
          ...base,
          name: 'Réveil des clients silencieux 💤',
          objective: 'Réactiver les clients dormants',
          subject: '{{prénom}}, on pense à vous ✨',
          body: `Bonjour {{prénom}},\n\nÇa fait un moment qu'on ne s'est pas vus — et franchement, ça nous manque.\n\nPour vous redonner envie de passer, une attention vous est réservée ce mois-ci.\n\nAu plaisir de vous retrouver,\n${sign(company, owner)}\n\nP.S. Elle est valable jusqu'à la fin du mois — après, il faudra qu'on trouve une autre excuse pour vous revoir 😄`,
          filters: { type: 'dormant', exclude_recent: true },
        },
      };
    case 'clickers':
      return {
        startStep: 1,
        preset: {
          ...base,
          name: 'Relance des curieux 🔥',
          objective: 'Convertir mes prospects intéressés',
          subject: '{{prénom}}, vous y avez jeté un œil… 👀',
          body: `Bonjour {{prénom}},\n\nNotre dernier message a retenu votre attention — bonne nouvelle : ce qui vous a plu est toujours là.\n\nPassez quand vous voulez, on vous a gardé une place.\n\nÀ très vite,\n${sign(company, owner)}`,
          filters: { type: 'hot', exclude_recent: true },
        },
      };
    case 'newcomers':
      return {
        startStep: 1,
        preset: {
          ...base,
          name: 'Bienvenue aux nouveaux 🌱',
          objective: 'Fidéliser mes clients',
          subject: 'Bienvenue chez nous, {{prénom}} 💛',
          body: `Bonjour {{prénom}},\n\nBienvenue parmi nous — et merci pour votre confiance !\n\nPour bien commencer, une petite attention de bienvenue vous attend lors de votre prochaine visite.\n\nAu plaisir,\n${sign(company, owner)}`,
          filters: { exclude_recent: true },
        },
      };
    case 'never_contacted':
      return {
        startStep: 1,
        preset: {
          ...base,
          name: 'Premier bonjour 👋',
          objective: 'Fidéliser mes clients',
          subject: 'Enchanté de vous compter parmi nous, {{prénom}} 👋',
          body: `Bonjour {{prénom}},\n\nVous faites partie de nos clients — et pourtant, on ne vous a encore jamais écrit. Il était temps de corriger ça !\n\nDésormais, vous serez parmi les premiers informés de nos nouveautés et attentions.\n\nEt pour marquer ce premier bonjour : une petite surprise vous attend lors de votre prochaine visite.\n\nAu plaisir,\n${sign(company, owner)}`,
          filters: { type: 'never_contacted', exclude_recent: true },
        },
      };
    case 'gender_insight': {
      const isF = o.gender === 'madame';
      return {
        startStep: 1,
        preset: {
          ...base,
          name: isF ? 'Offre spéciale pour elles 👩' : 'Offre spéciale pour eux 👨',
          objective: 'Lancer une offre ou une promotion',
          subject: isF ? 'Pensé pour vous, {{prénom}} 💐' : 'Pensé pour vous, {{prénom}} 🤝',
          body: `Bonjour {{prénom}},\n\nOn a préparé quelque chose qui devrait vous plaire — une attention pensée spécialement pour ${isF ? 'nos clientes' : 'nos clients'}.\n\nElle vous attend cette semaine.\n\nAu plaisir de vous voir,\n${sign(company, owner)}`,
          filters: { gender: [o.gender], exclude_recent: true },
        },
      };
    }
    case 'local':
      return {
        startStep: 1,
        preset: {
          ...base,
          name: `Campagne locale — ${o.city} 📍`,
          objective: 'Créer une campagne locale',
          subject: `Un rendez-vous près de chez vous, {{prénom}} 📍`,
          body: `Bonjour {{prénom}},\n\nBonne nouvelle pour ${o.city} et les environs : on a préparé quelque chose tout près de chez vous.\n\nPassez nous voir — entre voisins, c'est encore mieux.\n\nÀ très bientôt,\n${sign(company, owner)}`,
          filters: { city: o.city, exclude_recent: true },
        },
      };
    default:
      return null; // birthdays auto & inconnus → navigation simple
  }
}
